import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import baseUrl from "../../../../../api/baseUrl";

type AdminAuthModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  onVerified: () => void;
};

function AdminAuthModal({ isOpen, onCancel, onVerified }: AdminAuthModalProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (isOpen) {
      setEmail("");
      setPassword("");
      setErrorMessage("");
      setSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setErrorMessage("");

    try {
      await baseUrl.post("/branch/pos/verify-admin", { email, password });
      onVerified();
    } catch (error) {
      console.error("Error verifying admin credentials:", error);
      setErrorMessage("Invalid admin credentials.");
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setEmail("");
    setPassword("");
    setErrorMessage("");
    onCancel();
  };

  return (
    <Dialog open={isOpen} onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Admin Verification Required
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          Enter admin credentials to open and edit this bill for a sales
          return.
        </Typography>

        <TextField
          fullWidth
          autoFocus
          label="Admin Email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleSubmit();
            }
          }}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleSubmit();
            }
          }}
        />

        {errorMessage && (
          <Typography sx={{ mt: 2, color: "error.main", fontWeight: 600 }}>
            {errorMessage}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !email || !password}
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={{
            opacity: "1 !important",
            visibility: "visible !important",
            backgroundColor: "#1565c0 !important",
            color: "#fff !important",
            boxShadow: "none !important",
            "&:hover": {
              backgroundColor: "#0d47a1 !important",
            },
            "&.Mui-disabled": {
              backgroundColor: "rgba(0, 0, 0, 0.12) !important",
              color: "rgba(0, 0, 0, 0.26) !important",
            },
          }}
        >
          Verify
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AdminAuthModal;
